import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { GeneralTable } from 'sharedComponents/GeneralTable';
import { IconAssetOffline, IconAssetOnline, IconHistory } from 'svgIcons/MotionPortalIcons';
import { GeneralButton } from 'sharedComponents/GeneralButton';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { formatDateTime } from 'helpers/dateTimeFormatter';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { apiCallStatus } from 'helpers/constants';
import { useReportIssue } from 'sharedHooks/useReportIssue';

import { INITIAL_COUNT, PAGINATION_THRESHOLD } from './constants';

import '../style.scss';

const ConnectionHistoryModal = ({ isOpen, historyData, onClose, loadingStatus, error, onRetry }) => {
    const [showAll, setShowAll] = useState(false);
    const { t: translate } = useTranslation();
    const { showReportIssue } = useReportIssue();
    const allRecords = historyData?.connectionStatusHistory || [];
    const hasMore = allRecords.length > INITIAL_COUNT;
    const usePagination = allRecords.length > PAGINATION_THRESHOLD;

    useEffect(() => {
        if (!isOpen) {
            setShowAll(false);
        }
    }, [isOpen]);

    const columns = [
        {
            title: 'Connection Status',
            dataIndex: 'connectionStatus',
            render: (connectionStatus) =>
                <span className='connection-status-cell'>
                    {connectionStatus === 'Online' ? <IconAssetOnline /> : <IconAssetOffline />}
                    {connectionStatus}
                </span>
            ,
        },
        {
            title: 'Timestamp',
            dataIndex: 'transitionTimestamp',
            align: 'right',
            render: (transitionTimestamp) => formatDateTime(transitionTimestamp),
        },
    ];


    const getTitleConnectionHistoryModal = (translate) => {
        return (
            <div className='connection-history-modal-title' id='connection-history-modal-title'>
                <IconHistory /> {translate('ABB.Powertrain.Frontend.gatewayManagementConnectionHistoryModalTitle')}
            </div>
        );
    };

    const renderTable = () => {
        const data = showAll ? allRecords : allRecords.slice(0, INITIAL_COUNT);

        if (showAll && !usePagination) {
            return (
                <div className='connection-history-scroll'>
                    <GeneralTable
                        className='connection-history-table'
                        columns={columns}
                        data={data}
                        emptyText={translate('ABB.Powertrain.Frontend.gatewayManagementConnectionHistoryNotAvailable')}
                    />
                </div>
            );
        }

        return (
            <GeneralTable
                className='connection-history-table'
                columns={columns}
                data={data}
                emptyText={translate('ABB.Powertrain.Frontend.gatewayManagementConnectionHistoryNotAvailable')}
                paginationProps={showAll && usePagination ? { pageSize: 10, hideOnSinglePage: true } : null}
            />
        );
    };

    return (
        <GeneralDialog
            modalClassName='connection-history-modal'
            show={isOpen}
            title={getTitleConnectionHistoryModal(translate)}
            close={onClose}
            closeButton={true}
            id='connection-history-dialog'
        >
            <LoadingStatusWrapper loadingStatus={loadingStatus}>
                {loadingStatus === apiCallStatus.ERROR
                    ? <ErrorView
                        errorCode={error?.statusCode || error?.status || error?.response?.status || 503}
                        errorMessage={error?.message}
                        onRetry={onRetry}
                        onReportIssue={showReportIssue}
                    />
                    : <>
                        {renderTable()}
                        {!showAll && hasMore &&
                            <div className='connection-history-view-more'>
                                <GeneralButton
                                    type='primary'
                                    text={translate('ABB.Powertrain.Frontend.gatewayManagementConnectionHistoryViewMoreLabel')}
                                    onClick={() => setShowAll(true)}
                                />
                            </div>
                        }
                    </>
                }
            </LoadingStatusWrapper>
        </GeneralDialog>
    );
};

ConnectionHistoryModal.propTypes = {
    isOpen: PropTypes.bool,
    historyData: PropTypes.shape({
        connectionStatusHistory: PropTypes.arrayOf(
            PropTypes.shape({
                transitionTimestamp: PropTypes.string,
                connectionStatus: PropTypes.string,
            })
        ),
    }),
    onClose: PropTypes.func,
    loadingStatus: PropTypes.string,
    error: PropTypes.shape({
        message: PropTypes.string,
        statusCode: PropTypes.number,
        status: PropTypes.number,
        response: PropTypes.shape({
            status: PropTypes.number,
        }),
    }),
    onRetry: PropTypes.func,
};

export default ConnectionHistoryModal;
